import { normalizeCustomComparePeriods, normalizeTrendAnchor, STATISTICS_MAX_CUSTOM_COMPARE_PERIODS, STATISTICS_MAX_TREND_AXIS_POINTS } from './statisticsCompareConfig';
import { expandLineCompareAnchorToSegments, TREND_MODE, TREND_COMPARE_AXIS_UNIT, resolveTrendMonthlySpecForQuery, syncStatisticsLineCompareFilterFields } from './statisticsTrendFilters';
import { resolveOverviewPeriodUnit } from './overviewPeriodUnit';
import {
	resolveOrderCreatedMonthsForQuery,
	resolveOrderCreatedPeriodsForQuery,
	resolveCompletedOrderMonthsForQuery,
	resolveCompletedOrderPeriodsForQuery,
} from './periodQuickPickStaging';
import { resolveFilledStatisticsDatePeriodQuery } from './statisticsDatePeriods';
import { clampCompareAnalysisFiltersForPeriod } from './statisticsCompareHelpers';
import { resolveExplicitStatisticsProcessNodes, resolveStatisticsProcessNodeRelations, shouldSendStatisticsProcessNodesQuery } from './statisticsProcessNodes';
import { DEFAULT_ORDER_PRIMARY_PHASE_LENS } from '../../charts/pipeline/lib/orderPrimaryPhaseConfig';
import { ORDER_APPROVAL_STATUS_APPROVED_VALUES } from '../../../../utils/order/orderStatus';
import { ORDER_LIST_DATE_RANGE_PRESETS } from '../../../../utils/order/orderListDateRangePresets';
// 订单列表每页默认展示 8 条订单（与总览一锅出分页一致）
/** 对齐 Falcon RecentPurchases perPage: 7 */
export const STATISTICS_ORDERS_LIST_PAGE_SIZE = 7;

/** 仅折线对比专用字段（与顶栏业务筛选 scope 分离） */
export const TREND_FIELD_KEYS = [
	'trendAnchor',
	'customComparePeriods',
	'trendMode',
	'trendMonthlyYears',
	'trendMonthlyYoy',
	'trendCompareAxisUnit',
	'trendAxisMode',
	'trendDateField',
	'trendYAxisMetric',
	'trendYAxisMetrics',
	'trendChartType',
	'trendLineShape',
	'trendBarOrientation',
	'trendMetricSeriesTypes',
	'trendYearSeriesColors',
];

// 订单状态过滤器默认值
export const STATISTICS_ORDER_STATUS_FILTER_DEFAULTS = {
	notDelivery: true,
	deliveredNotAccept: true,
	acceptedOrder: true,
	settledOrder: true,
	outstandingOrder: true,
};

// 业务统计默认值
export const STATISTICS_BASE_FILTER_DEFAULTS = {
	startDate: null,
	endDate: null,
	completedStartDate: null,
	completedEndDate: null,
	completedDateAnchor: null,
	dateRelation: 'and',
	processNodes: ['created'],
	processNodeRelations: [],
	datePeriods: null,
	saleUser: [],
	assistantUser: [],
	suppliers: [],
	brands: [],
	categories: [],
	products: [],
	customers: [],
	customerContacts: [],
	customerProvince: [],
	customerCity: [],
	approvalStatuses: [...ORDER_APPROVAL_STATUS_APPROVED_VALUES],
	primaryPhases: [],
	paymentStatuses: [],
	deliveryStatuses: [],
	isCompleted: '',
	statusFilterAdditive: false,
	compareYoy: true,
	compareMom: true,
	customComparePeriods: [],
	trendAnchor: null,
	trendMode: TREND_MODE.PERIODS,
	trendMonthlyYears: [],
	trendMonthlyYoy: true,
	trendCompareAxisUnit: null,
	trendAxisMode: 'time',
	trendDateField: 'createdAt',
	trendYAxisMetric: 'count',
	trendYAxisMetrics: ['count'],
	trendChartType: 'bar',
	trendLineShape: 'smooth',
	trendBarOrientation: 'vertical',
	trendMetricSeriesTypes: {},
	trendYearSeriesColors: {},
};

export const createDefaultOrdersListQuery = () => ({
	page: 1,
	limit: STATISTICS_ORDERS_LIST_PAGE_SIZE,
	phase: null,
	dimension: null,
	dimensionKey: null,
	statusField: null,
	statusValue: null,
	geoProvince: null,
	geoCityCode: null,
	/** 经营总览订单列表本地状态筛选（不影响 KPI） */
	paymentStatuses: [],
	deliveryStatuses: [],
	fulfillmentComplete: false,
	pendingReview: false,
	inProgress: false,
	verified: false,
});

/** 序列化过滤器值为逗号分隔的字符串 */
const serializeFilterValue = (value) => {
	if (Array.isArray(value)) return value.length > 0 ? value.join(',') : undefined;
	if (value === '' || value == null) return undefined;
	return value;
};

/** 序列化日期为 ISO 字符串 */
const serializeStatisticsDate = (value) => {
	if (!value) return undefined;
	if (value instanceof Date) return value.toISOString();
	return new Date(value).toISOString();
};

/** 默认下单区间字段：以当天为准的「本年」（1/1～今日）；trendAnchor 由 apply 链路补齐 */
export const buildStatisticsDefaultOrderDateFields = () => {
	const preset = ORDER_LIST_DATE_RANGE_PRESETS.find((item) => item.id === 'thisYear');
	if (!preset) {
		return { startDate: null, endDate: null, trendAnchor: null, datePeriods: null };
	}
	const { from, to } = preset.getRange();
	return {
		startDate: from,
		endDate: to,
		trendAnchor: null,
		datePeriods: null,
	};
};

/** 业务统计默认：当年下单区间（不再默认全库「全部订单」） */
export const createStatisticsFilterDefaults = () => ({
	...STATISTICS_BASE_FILTER_DEFAULTS,
	...buildStatisticsDefaultOrderDateFields(),
});

/** 订单流程分析统计 API query 参数（日期、人员、对比、折线 year-months 等） */
export const buildStatisticsQueryParams = (filters = {}, { includeOrders = true, includeOrderIds = false, ordersList = null, compositionCompareSeries = false, ordersOnly = false, includeCompares = true, comparesOnly = false } = {}) => {
	const hasPhaseFilter = Array.isArray(filters.primaryPhases) && filters.primaryPhases.length > 0;
	const scopedFilters = syncStatisticsLineCompareFilterFields(
		clampCompareAnalysisFiltersForPeriod({
			...filters,
			/** 节点筛选匹配口径固定 operational */
			primaryPhaseLens: hasPhaseFilter ? DEFAULT_ORDER_PRIMARY_PHASE_LENS : undefined,
		}),
	);

	const customComparePeriods = normalizeCustomComparePeriods(scopedFilters.customComparePeriods);
	const trendAnchor = normalizeTrendAnchor(scopedFilters.trendAnchor);
	const completedDateAnchor = normalizeTrendAnchor(scopedFilters.completedDateAnchor);
	/** 多段下单日（与/或）优先于 year-months：否则只带第一段 startDate/endDate */
	const filledDatePeriods = resolveFilledStatisticsDatePeriodQuery(scopedFilters);
	const useDatePeriodQuery = filledDatePeriods.periods.length > 1;
	const monthlySpec = resolveTrendMonthlySpecForQuery(scopedFilters);
	/** 组成对比多层圈须走 comparePeriods，勿被「整年+对比段」升成 year-months 吃掉 */
	const isYearMonthsMode = Boolean(monthlySpec) && !compositionCompareSeries && !useDatePeriodQuery;
	const monthlyYears = monthlySpec?.years ?? [];
	/** 制作图「按年」整段一点；有显式 periodAxis（按月/季拆点）时允许展开，不跟当年/当月/当季绑死 */
	const overviewPeriodUnit = resolveOverviewPeriodUnit(scopedFilters.startDate, scopedFilters.endDate, trendAnchor);
	const hasExplicitPeriodAxis =
		trendAnchor?.periodAxis === 'month' || trendAnchor?.periodAxis === 'quarter';
	const skipAnchorExpand =
		scopedFilters.trendCompareAxisUnit === TREND_COMPARE_AXIS_UNIT.YEAR ||
		(!hasExplicitPeriodAxis &&
			(overviewPeriodUnit === 'year' || overviewPeriodUnit === 'month' || overviewPeriodUnit === 'quarter'));
	const anchorSegments = !isYearMonthsMode && !skipAnchorExpand ? expandLineCompareAnchorToSegments(trendAnchor) : null;
	const isSegmentedAnchor = Boolean(anchorSegments?.length);
	const segmentedCustomSlots = isSegmentedAnchor ? Math.max(0, STATISTICS_MAX_TREND_AXIS_POINTS - anchorSegments.length) : STATISTICS_MAX_CUSTOM_COMPARE_PERIODS;
	const comparePeriodItems = isSegmentedAnchor
		? [
				...anchorSegments.map((segment) => ({
					startDate: segment.startDate,
					endDate: segment.endDate,
					label: segment.label?.trim() || undefined,
					segmentKind: 'anchor',
				})),
				...customComparePeriods.slice(0, segmentedCustomSlots).map((item) => ({
					startDate: item.startDate,
					endDate: item.endDate,
					label: item.label?.trim() || undefined,
				})),
			].slice(0, STATISTICS_MAX_TREND_AXIS_POINTS)
		: customComparePeriods;
	const comparePeriodsPayload =
		!isYearMonthsMode && comparePeriodItems.length > 0
			? JSON.stringify(
					comparePeriodItems.map((item) => ({
						startDate: serializeStatisticsDate(item.startDate),
						endDate: serializeStatisticsDate(item.endDate),
						label: item.label?.trim() || undefined,
						...(item.segmentKind === 'anchor' ? { segmentKind: 'anchor' } : {}),
					})),
				)
			: undefined;
	const compareAnchorPayload =
		!isYearMonthsMode && trendAnchor && !isSegmentedAnchor
			? JSON.stringify({
					startDate: serializeStatisticsDate(trendAnchor.startDate),
					endDate: serializeStatisticsDate(trendAnchor.endDate),
					label: trendAnchor.label || undefined,
				})
			: undefined;

	const trendMonthlyYears = isYearMonthsMode ? serializeFilterValue(monthlyYears) : undefined;
	const trendMonthlyMonths =
		isYearMonthsMode && monthlySpec?.granularity !== 'quarter' && monthlySpec?.months?.length && monthlySpec.months.length < 12
			? serializeFilterValue(monthlySpec.months.map((month) => month + 1))
			: undefined;
	const trendMonthlyQuarters =
		isYearMonthsMode && monthlySpec?.granularity === 'quarter' && monthlySpec?.quarters?.length && monthlySpec.quarters.length < 4
			? serializeFilterValue(monthlySpec.quarters.map((quarter) => quarter + 1))
			: undefined;
	/** year-months 由 trendMonthlyYears 扩成年跨度；跳选月会按 startDate 单年展开，不可带到主查询/同比轴 */
	const orderCreatedPeriods = useDatePeriodQuery
		? filledDatePeriods.periods
		: isYearMonthsMode
			? undefined
			: resolveOrderCreatedPeriodsForQuery({ trendAnchor });
	const orderCreatedMonths = isYearMonthsMode
		? undefined
		: orderCreatedPeriods
			? undefined
			: resolveOrderCreatedMonthsForQuery({
					startDate: scopedFilters.startDate,
					endDate: scopedFilters.endDate,
					trendAnchor,
				});
	const completedOrderPeriods = isYearMonthsMode ? undefined : resolveCompletedOrderPeriodsForQuery({ completedDateAnchor });
	const completedOrderMonths = isYearMonthsMode
		? undefined
		: completedOrderPeriods
			? undefined
			: resolveCompletedOrderMonthsForQuery({
					startDate: scopedFilters.completedStartDate,
					endDate: scopedFilters.completedEndDate,
					completedDateAnchor,
				});
	const processNodesForQuery = shouldSendStatisticsProcessNodesQuery(scopedFilters)
		? resolveExplicitStatisticsProcessNodes(scopedFilters)
		: [];
	const processNodeRelationsForQuery =
		processNodesForQuery.length > 1 ? resolveStatisticsProcessNodeRelations(scopedFilters, processNodesForQuery) : [];

	return {
		includeOrders: includeOrders ? undefined : 'false',
		includeOrderIds: includeOrderIds ? 'true' : undefined,
		ordersOnly: ordersOnly ? 'true' : undefined,
		comparesOnly: comparesOnly ? 'true' : undefined,
		includeCompares: !comparesOnly && !includeCompares ? 'false' : undefined,
		compositionCompareSeries: compositionCompareSeries || filters.compositionCompareSeries ? 'true' : undefined,
		...(includeOrders && ordersList
			? {
					ordersPage: ordersList.page,
					ordersLimit: ordersList.limit,
					...(ordersList.phase ? { ordersPhase: ordersList.phase } : {}),
					...(ordersList.dimension && ordersList.dimensionKey ? { ordersDimension: ordersList.dimension, ordersDimensionKey: ordersList.dimensionKey } : {}),
					...(ordersList.statusField && ordersList.statusValue != null && ordersList.statusValue !== ''
						? { ordersStatusField: ordersList.statusField, ordersStatusValue: ordersList.statusValue }
						: {}),
					...(ordersList.geoCityCode ? { ordersGeoCityCode: ordersList.geoCityCode } : {}),
					...(ordersList.geoProvince && !ordersList.geoCityCode ? { ordersGeoProvince: ordersList.geoProvince } : {}),
					...(Array.isArray(ordersList.paymentStatuses) && ordersList.paymentStatuses.length > 0
						? { ordersPaymentStatuses: ordersList.paymentStatuses.join(',') }
						: {}),
					...(Array.isArray(ordersList.deliveryStatuses) && ordersList.deliveryStatuses.length > 0
						? { ordersDeliveryStatuses: ordersList.deliveryStatuses.join(',') }
						: {}),
					...(ordersList.fulfillmentComplete ? { ordersFulfillmentComplete: 'true' } : {}),
					...(ordersList.pendingReview ? { ordersPendingReview: 'true' } : {}),
					...(ordersList.inProgress ? { ordersInProgress: 'true' } : {}),
					...(ordersList.verified ? { ordersVerified: 'true' } : {}),
				}
			: {}),
		startDate: serializeStatisticsDate(scopedFilters.startDate),
		endDate: serializeStatisticsDate(scopedFilters.endDate),
		orderCreatedMonths: serializeFilterValue(orderCreatedMonths),
		orderCreatedPeriods: orderCreatedPeriods
			? JSON.stringify(
					orderCreatedPeriods.map((period) => ({
						startDate: serializeStatisticsDate(period.startDate),
						endDate: serializeStatisticsDate(period.endDate),
						...(period.orderCreatedMonths?.length ? { orderCreatedMonths: period.orderCreatedMonths } : {}),
					})),
				)
			: undefined,
		completedStartDate: serializeStatisticsDate(scopedFilters.completedStartDate),
		completedEndDate: serializeStatisticsDate(scopedFilters.completedEndDate),
		dateRelation: scopedFilters.dateRelation === 'or' ? 'or' : undefined,
		processNodes: serializeFilterValue(processNodesForQuery),
		processNodeRelations: serializeFilterValue(processNodeRelationsForQuery),
		completedOrderMonths: serializeFilterValue(completedOrderMonths),
		completedOrderPeriods: completedOrderPeriods
			? JSON.stringify(
					completedOrderPeriods.map((period) => ({
						startDate: serializeStatisticsDate(period.startDate),
						endDate: serializeStatisticsDate(period.endDate),
						...(period.orderCreatedMonths?.length ? { completedOrderMonths: period.orderCreatedMonths } : {}),
					})),
				)
			: undefined,
		saleUser: serializeFilterValue(scopedFilters.saleUser),
		assistantUser: serializeFilterValue(scopedFilters.assistantUser),
		suppliers: serializeFilterValue(scopedFilters.suppliers),
		brands: serializeFilterValue(scopedFilters.brands),
		categories: serializeFilterValue(scopedFilters.categories),
		products: serializeFilterValue(scopedFilters.products),
		customers: serializeFilterValue(scopedFilters.customers),
		customerContacts: serializeFilterValue(scopedFilters.customerContacts),
		customerProvince: serializeFilterValue(scopedFilters.customerProvince),
		customerCity: serializeFilterValue(scopedFilters.customerCity),
		approvalStatuses: serializeFilterValue(ORDER_APPROVAL_STATUS_APPROVED_VALUES),
		primaryPhases: serializeFilterValue(scopedFilters.primaryPhases),
		/** 仅在有流程节点筛选时带上；固定 operational */
		primaryPhaseLens: hasPhaseFilter ? DEFAULT_ORDER_PRIMARY_PHASE_LENS : undefined,
		paymentStatuses: serializeFilterValue(scopedFilters.paymentStatuses),
		deliveryStatuses: serializeFilterValue(scopedFilters.deliveryStatuses),
		// 统计查询固定为已审批订单，不按关单状态收窄
		isCompleted: undefined,
		compareYoy: scopedFilters.compareYoy === false ? 'false' : undefined,
		compareMom: scopedFilters.compareMom === false ? 'false' : undefined,
		comparePeriods: comparePeriodsPayload,
		compareAnchor: compareAnchorPayload,
		trendMode: isYearMonthsMode ? TREND_MODE.YEAR_MONTHS : undefined,
		trendMonthlyYears,
		trendMonthlyMonths,
		trendMonthlyQuarters,
		trendDateField: scopedFilters.trendDateField === 'completedAt' ? 'completedAt' : undefined,
	};
};

/** 顶栏业务筛选 scope（含下单/完成日期、人员、产品等；不含折线对比段配置） */
export const pickStatisticsScopeFields = (filters = {}) => {
	const result = {};
	for (const key of Object.keys(STATISTICS_BASE_FILTER_DEFAULTS)) {
		if (TREND_FIELD_KEYS.includes(key)) continue;
		const fallback = STATISTICS_BASE_FILTER_DEFAULTS[key];
		result[key] = filters[key] ?? fallback;
	}
	return result;
};

export const statisticsScopeFieldsEqual = (left, right) => JSON.stringify(pickStatisticsScopeFields(left)) === JSON.stringify(pickStatisticsScopeFields(right));
